import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useAdminContext } from '@/hooks/useAdminContext';
import {
  ArrowLeft, Copy, RefreshCw, Eye, TrendingUp, TrendingDown, Minus,
  Check, Lightbulb, Brain, Plus, Trash2, BookOpen, Users, Settings,
  FileText, Upload,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { UserActionsDropdown } from '@/components/admin/UserActionsDropdown';
import { getInitials } from '@/lib/utils';

// ── Types ────────────────────────────────────────────────────────

interface OrgDetail {
  org_id: string;
  org_name: string;
  status: string;
  internal_notes: string | null;
  invite_code: string;
  scored_7d: number;
  scored_30d: number;
  wau_7d: number;
  fsr_lift_30d: number;
  pct_users_improving: number;
  last_activity: string | null;
  active_users: number;
  total_users: number;
}

interface DeptData {
  department: string;
  scored_7d: number;
  wau: number;
  avg_rubric_score: number;
  avg_fsr: number;
}

interface UserData {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  department: string;
  is_active: boolean;
  last_active: string | null;
  scored_30d: number;
  avg_score_30d: number;
  fsr: number;
  workflows_enabled: boolean;
}

interface KeyPerson {
  localId: string;
  user_id: string | null;    // null = not a Cosmo user
  email: string;
  name: string;
  title: string;
  department: string;
  isFromProfile: boolean;    // true = linked to org membership
  disposition: string;
  influence_level: string;
  ai_fluency_level: string;
  notes: string;
}

// ── Constants ────────────────────────────────────────────────────

const DEPARTMENTS = ['Business Development', 'Consulting', 'Strategy', 'Events', 'Ops', 'Leadership'];
const ROLES = ['user', 'client_admin', 'exec'];

const COMPANY_SIZES = [
  '1–10', '11–50', '51–200', '201–500', '501–1,000', '1,001–5,000', '5,000+',
];

const DISPOSITIONS = [
  { value: 'champion',     label: 'Champion' },
  { value: 'council_admin',label: 'Council Admin' },
  { value: 'stakeholder',  label: 'Stakeholder' },
  { value: 'skeptical',    label: 'Skeptical' },
  { value: 'blocker',      label: 'Blocker' },
];

const INFLUENCE_LEVELS = [
  { value: 'high',   label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low',    label: 'Low' },
];

const AI_FLUENCY_LEVELS = [
  { value: 'L0', label: 'L0 — Never used AI' },
  { value: 'L1', label: 'L1 — Tried it a few times' },
  { value: 'L2', label: 'L2 — Uses it occasionally' },
  { value: 'L3', label: 'L3 — Regular user' },
  { value: 'L4', label: 'L4 — Power user' },
];

interface DocFile {
  localId: string;
  name: string;
  file?: File;      // pending — not yet uploaded
  path?: string;    // storage path — already uploaded
  url?: string;     // public URL
  size: number;
  fileType: string;
}

const ACCEPTED_DOC_TYPES = '.pdf,.docx,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const MAX_DOCS = 10;

function formatBytes(bytes: number) {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function newPerson(): KeyPerson {
  return {
    localId: Math.random().toString(36).slice(2),
    user_id: null, email: '', name: '', title: '', department: '',
    isFromProfile: false, disposition: 'stakeholder',
    influence_level: 'medium', ai_fluency_level: 'L1', notes: '',
  };
}

// ── Main component ───────────────────────────────────────────────

export default function AdminOrgDetail() {
  const { orgId }        = useParams<{ orgId: string }>();
  const navigate         = useNavigate();
  const [searchParams]   = useSearchParams();
  const { startImpersonation } = useAdminContext();

  // Tab
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') ?? 'overview');

  // Org / users state
  const [org, setOrg]                     = useState<OrgDetail | null>(null);
  const [departments, setDepartments]     = useState<DeptData[]>([]);
  const [users, setUsers]                 = useState<UserData[]>([]);
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [editedStatus, setEditedStatus]   = useState('');
  const [editedNotes, setEditedNotes]     = useState('');
  const [editedName, setEditedName]       = useState('');
  const [copiedCode, setCopiedCode]       = useState(false);

  // Edit user modal
  const [editingUser, setEditingUser]     = useState<UserData | null>(null);
  const [editUserRole, setEditUserRole]   = useState('');
  const [editUserDept, setEditUserDept]   = useState('');
  const [editUserWorkflows, setEditUserWorkflows] = useState(false);

  // ── Knowledge state ──────────────────────────────────────────────

  const [kProfile, setKProfile] = useState({
    industry: '', company_size: '', hq_location: '', plan_details: '',
  });
  const [kPeople, setKPeople]   = useState<KeyPerson[]>([]);
  const [kGoals, setKGoals]     = useState({
    primary_goal: '', success_metric_90d: '', key_deadline: '', expansion_trigger: '',
  });
  const [kDynamics, setKDynamics] = useState({ notes: '' });
  const [kDocs, setKDocs]       = useState<DocFile[]>([]);
  const [kSaving, setKSaving]   = useState<string | null>(null);
  const [showPersonPicker, setShowPersonPicker] = useState(false);
  const fileInputRef             = useRef<HTMLInputElement>(null);

  // ── Data loading ─────────────────────────────────────────────────

  const fetchData = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [orgRes, deptRes, usersRes, knowledgeRes] = await Promise.all([
        supabase.rpc('get_org_admin_detail',      { _org_id: orgId }),
        supabase.rpc('get_org_departments_admin', { _org_id: orgId }),
        supabase.rpc('get_org_users_admin',       { _org_id: orgId }),
        supabase.from('org_knowledge_store').select('*').eq('org_id', orgId),
      ]);

      if (orgRes.error)   throw orgRes.error;
      if (deptRes.error)  throw deptRes.error;
      if (usersRes.error) throw usersRes.error;

      const orgData = orgRes.data?.[0];
      if (orgData) {
        setOrg(orgData);
        setEditedStatus(orgData.status);
        setEditedNotes(orgData.internal_notes || '');
        setEditedName(orgData.org_name);
      }
      setDepartments(deptRes.data || []);
      setUsers(usersRes.data || []);

      // Parse knowledge rows into form state
      const rows = knowledgeRes.data ?? [];

      const profileRow = rows.find(r => r.field_type === 'org_profile');
      if (profileRow?.content_json) {
        const j = profileRow.content_json as Record<string, string>;
        setKProfile({
          industry:     j.industry     ?? '',
          company_size: j.company_size ?? '',
          hq_location:  j.hq_location  ?? '',
          plan_details: j.plan_details ?? '',
        });
      }

      const peopleRows = rows.filter(r => r.field_type === 'key_person');
      setKPeople(
        peopleRows.map(r => {
          const j = r.content_json as Record<string, any>;
          return {
            localId:          r.id,
            user_id:          j.user_id          ?? null,
            email:            j.email            ?? '',
            name:             j.name             ?? '',
            title:            j.title            ?? '',
            department:       j.department       ?? '',
            isFromProfile:    !!j.user_id,
            disposition:      j.disposition      ?? 'stakeholder',
            influence_level:  j.influence_level  ?? 'medium',
            ai_fluency_level: j.ai_fluency_level ?? 'L1',
            notes:            j.notes            ?? '',
          };
        })
      );

      const goalRow = rows.find(r => r.field_type === 'goal');
      if (goalRow?.content_json) {
        const j = goalRow.content_json as Record<string, string>;
        setKGoals({
          primary_goal:      j.primary_goal       ?? '',
          success_metric_90d:j.success_metric_90d ?? '',
          key_deadline:      j.key_deadline       ?? '',
          expansion_trigger: j.expansion_trigger  ?? '',
        });
      }

      const dynamicRow = rows.find(r => r.field_type === 'dynamic');
      if (dynamicRow?.content_json) {
        const j = dynamicRow.content_json as Record<string, any>;
        setKDynamics({ notes: j.notes ?? '' });
        // Load previously uploaded documents
        if (Array.isArray(j.documents)) {
          setKDocs(
            (j.documents as any[]).map(d => ({
              localId:  Math.random().toString(36).slice(2),
              name:     d.name     ?? 'Document',
              path:     d.path,
              url:      d.url,
              size:     d.size     ?? 0,
              fileType: d.fileType ?? 'application/octet-stream',
            }))
          );
        }
      }
    } catch (error) {
      console.error('Error fetching org data:', error);
      toast.error('Failed to load organization');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [orgId]);

  // ── Org settings save ─────────────────────────────────────────────

  const saveOrgChanges = async () => {
    if (!orgId) return;
    setSaving(true);
    try {
      const { error } = await supabase.rpc('update_org_admin', {
        _org_id: orgId, _status: editedStatus,
        _internal_notes: editedNotes || null, _name: editedName,
      });
      if (error) throw error;
      toast.success('Organization updated');
      fetchData();
    } catch (error) {
      console.error('Error updating org:', error);
      toast.error('Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  // ── Knowledge save (per section) ──────────────────────────────────

  const saveKnowledgeSection = async (
    fieldType: 'org_profile' | 'key_person' | 'goal' | 'dynamic',
    rows: Array<{ content_json: object }>,
  ) => {
    if (!orgId) return;
    setKSaving(fieldType);
    try {
      // Delete existing rows for this field_type + org, then re-insert
      const { error: delErr } = await supabase
        .from('org_knowledge_store')
        .delete()
        .eq('org_id', orgId)
        .eq('field_type', fieldType);
      if (delErr) throw delErr;

      if (rows.length > 0) {
        const { error: insErr } = await supabase
          .from('org_knowledge_store')
          .insert(
            rows.map(r => ({
              org_id: orgId,
              field_type: fieldType,
              source_label: 'Admin edit',
              content_json: r.content_json,
            })) as any
          );
        if (insErr) throw insErr;
      }

      // Ensure knowledge_store_complete is true if any section has data
      await supabase
        .from('organizations')
        .update({ knowledge_store_complete: true })
        .eq('id', orgId);

      // Trigger proactive engine — Cosmo should react to new knowledge
      supabase.functions.invoke('cosmo-proactive-engine', {
        body: { org_id: orgId, trigger: 'knowledge_updated' },
      }).then(({ error }) => {
        if (error) console.warn('Proactive engine trigger failed:', error);
        else console.log('[Knowledge save] Proactive engine triggered for', orgId);
      }).catch(() => {}); // fire and forget

      toast.success('Saved');
    } catch (error) {
      console.error('Knowledge save error:', error);
      toast.error('Failed to save');
    } finally {
      setKSaving(null);
    }
  };

  // ── Convenience save wrappers ─────────────────────────────────────

  const saveProfile = () =>
    saveKnowledgeSection('org_profile', [{ content_json: kProfile }]);

  const savePeople = () =>
    saveKnowledgeSection(
      'key_person',
      kPeople
        .filter(p => p.name.trim())
        .map(p => ({
          content_json: {
            user_id:          p.user_id,
            email:            p.email,
            name:             p.name,
            title:            p.title,
            department:       p.department,
            disposition:      p.disposition,
            influence_level:  p.influence_level,
            ai_fluency_level: p.ai_fluency_level,
            notes:            p.notes,
          },
        }))
    );

  const saveGoals = () =>
    saveKnowledgeSection('goal', [{ content_json: kGoals }]);

  const saveDynamics = async () => {
    if (!orgId) return;
    setKSaving('dynamic');
    try {
      // Upload any pending files (those with a File object, not yet in storage)
      const resolvedDocs = await Promise.all(
        kDocs.map(async doc => {
          if (doc.file) {
            const safeName = doc.name.replace(/[^a-zA-Z0-9._-]/g, '_');
            const storagePath = `${orgId}/${Date.now()}_${safeName}`;
            const contentType = doc.fileType || 'application/octet-stream';
            const { error: upErr } = await supabase.storage
              .from('org-documents')
              .upload(storagePath, doc.file, { contentType, upsert: false });
            if (upErr) {
              console.error('Storage upload error (full):', upErr);
              toast.error(`Failed to upload ${doc.name}: ${upErr.message}`);
              return null;
            }
            const { data: { publicUrl } } = supabase.storage
              .from('org-documents')
              .getPublicUrl(storagePath);
            return { name: doc.name, path: storagePath, url: publicUrl, size: doc.size, fileType: doc.fileType };
          }
          // Already uploaded — preserve metadata
          return { name: doc.name, path: doc.path, url: doc.url, size: doc.size, fileType: doc.fileType };
        })
      );

      const documents = resolvedDocs.filter(Boolean);

      // Delete old row + insert new one with updated content
      const { error: delErr } = await supabase
        .from('org_knowledge_store')
        .delete()
        .eq('org_id', orgId)
        .eq('field_type', 'dynamic');
      if (delErr) throw delErr;

      const { error: insErr } = await supabase
        .from('org_knowledge_store')
        .insert([{
          org_id: orgId,
          field_type: 'dynamic',
          source_label: 'Admin edit',
          content_json: { notes: kDynamics.notes, documents } as any,
        }]);
      if (insErr) throw insErr;

      await supabase.from('organizations')
        .update({ knowledge_store_complete: true }).eq('id', orgId);

      // Update local kDocs — clear File references now they're uploaded
      setKDocs(
        documents
          .filter((d): d is NonNullable<typeof d> => d !== null)
          .map(d => ({
            localId:  Math.random().toString(36).slice(2),
            name:     d.name ?? '',
            path:     d.path,
            url:      d.url,
            size:     d.size ?? 0,
            fileType: d.fileType ?? '',
          }))
      );
      toast.success('Organization Insights saved');
    } catch (err: any) {
      console.error('Save insights error:', err);
      toast.error('Failed to save');
    } finally {
      setKSaving(null);
    }
  };

  // ── People helpers ────────────────────────────────────────────────

  const updatePerson = (localId: string, field: keyof KeyPerson, value: string) =>
    setKPeople(prev => prev.map(p => p.localId === localId ? { ...p, [field]: value } : p));

  const removePerson = (localId: string) =>
    setKPeople(prev => prev.filter(p => p.localId !== localId));

  const availableUsers = users.filter(u => !kPeople.some(kp => kp.user_id === u.user_id));

  const addPersonFromProfile = (user: UserData) => {
    setKPeople(prev => [...prev, {
      localId:          Math.random().toString(36).slice(2),
      user_id:          user.user_id,
      email:            user.email,
      name:             user.full_name || user.email,
      title:            '',
      department:       user.department ?? '',
      isFromProfile:    true,
      disposition:      'stakeholder',
      influence_level:  'medium',
      ai_fluency_level: 'L1',
      notes:            '',
    }]);
    setShowPersonPicker(false);
  };

  // ── Other handlers ────────────────────────────────────────────────

  const copyInviteLink = async () => {
    if (!org?.invite_code) return;
    const url = `${window.location.origin}/signup?inviteCode=${encodeURIComponent(org.invite_code)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
      toast.success('Invite link copied!');
    } catch { toast.error('Failed to copy'); }
  };

  const handleViewAs = () => {
    if (!org) return;
    startImpersonation(org.org_id, org.org_name, 'client_admin');
    navigate('/exec-dashboard');
  };

  const handleTriggerBackfill = async () => {
    if (!orgId) return;
    try {
      toast.loading('Running backfill...');
      const { data, error } = await supabase.functions.invoke('backfill-fluency-snapshots', { body: { org_id: orgId } });
      if (error) throw error;
      toast.dismiss();
      toast.success(`Backfill complete: ${data.updated || 0} users updated`);
      fetchData();
    } catch (error) {
      console.error('Backfill error:', error);
      toast.dismiss();
      toast.error('Backfill failed');
    }
  };

  const handleGenerateIdeas = async () => {
    if (!orgId) return;
    try {
      toast.loading('Generating ideas...');
      const { data, error } = await supabase.functions.invoke('generate-prompt-ideas', {
        body: { org_id: orgId, triggered_by: 'manual', force: true },
      });
      if (error) throw error;
      toast.dismiss();
      const count = data?.ideas_json?.length || 0;
      toast.success(`Generated ${count} ideas for this org`);
    } catch (error) {
      console.error('Generate ideas error:', error);
      toast.dismiss();
      toast.error('Failed to generate ideas');
    }
  };

  const openEditUser = (user: UserData) => {
    setEditingUser(user);
    setEditUserRole(user.role);
    setEditUserDept(user.department);
    setEditUserWorkflows(user.workflows_enabled);
  };

  const saveUserChanges = async () => {
    if (!editingUser) return;
    try {
      const { error } = await supabase.functions.invoke('admin-update-user', {
        body: {
          user_id: editingUser.user_id,
          role: editUserRole,
          department: editUserDept,
          workflows_enabled: editUserWorkflows,
        },
      });
      if (error) throw error;
      toast.success('User updated');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const getLiftIndicator = (lift: number) => {
    if (lift > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (lift < 0) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  // ── Loading / not found ───────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Organization not found</p>
        <Button variant="link" onClick={() => navigate('/admin')}>Back to Organizations</Button>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Back */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Organizations
      </Button>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">{org.org_name}</h1>
          <p className="text-muted-foreground text-sm">
            {org.active_users} active users · Last activity{' '}
            {org.last_activity ? formatDistanceToNow(new Date(org.last_activity), { addSuffix: true }) : 'Never'}
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Button variant="outline" size="sm" onClick={copyInviteLink} className="justify-start sm:justify-center">
            {copiedCode ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
            {copiedCode ? 'Copied!' : 'Copy Invite Link'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleTriggerBackfill} className="justify-start sm:justify-center">
            <RefreshCw className="h-4 w-4 mr-2" />Run Backfill
          </Button>
          <Button variant="outline" size="sm" onClick={handleGenerateIdeas} className="justify-start sm:justify-center">
            <Lightbulb className="h-4 w-4 mr-2" />Generate Ideas
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => navigate(`/admin/growth/thinking/${orgId}`)}
            className="justify-start sm:justify-center"
          >
            <Brain className="h-4 w-4 mr-2" />Cosmo's Thinking
          </Button>
          <Button size="sm" onClick={handleViewAs} className="justify-start sm:justify-center">
            <Eye className="h-4 w-4 mr-2" />View as Admin
          </Button>
        </div>
      </div>

      {/* ── Tabbed content ── */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="knowledge" className="gap-1.5">
            <BookOpen className="h-3.5 w-3.5" />Knowledge
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-1.5">
            <Users className="h-3.5 w-3.5" />Users
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-1.5">
            <Settings className="h-3.5 w-3.5" />Settings
          </TabsTrigger>
        </TabsList>

        {/* ── Overview ── */}
        <TabsContent value="overview" className="space-y-6 mt-4">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">Submissions (7d / 30d)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl sm:text-2xl font-bold">
                  {org.scored_7d}{' '}
                  <span className="text-muted-foreground text-base sm:text-lg">/ {org.scored_30d}</span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">WAU (7d)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl sm:text-2xl font-bold">{org.wau_7d}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">FSR Lift (30d)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  {getLiftIndicator(org.fsr_lift_30d)}
                  <span className={`text-xl sm:text-2xl font-bold ${org.fsr_lift_30d > 0 ? 'text-green-600' : org.fsr_lift_30d < 0 ? 'text-red-600' : ''}`}>
                    {org.fsr_lift_30d > 0 ? '+' : ''}{org.fsr_lift_30d}
                  </span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">% Users Improving</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl sm:text-2xl font-bold">
                  {org.pct_users_improving > 0 ? `${org.pct_users_improving}%` : '-'}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Department Breakdown */}
          <Card>
            <CardHeader><CardTitle>Department Breakdown</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Department</TableHead>
                    <TableHead>Submissions (7d)</TableHead>
                    <TableHead>WAU</TableHead>
                    <TableHead>Avg Rubric Score</TableHead>
                    <TableHead>Avg FSR</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {departments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">No department data</TableCell>
                    </TableRow>
                  ) : (
                    departments.map(dept => (
                      <TableRow key={dept.department}>
                        <TableCell className="font-medium">{dept.department}</TableCell>
                        <TableCell>{dept.scored_7d}</TableCell>
                        <TableCell>{dept.wau}</TableCell>
                        <TableCell>{dept.avg_rubric_score || '-'}</TableCell>
                        <TableCell>{dept.avg_fsr || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Knowledge ── */}
        <TabsContent value="knowledge" className="space-y-6 mt-4">

          {/* Org Profile */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-base">Org Profile</CardTitle>
              <Button
                size="sm" onClick={saveProfile}
                disabled={kSaving === 'org_profile'}
              >
                {kSaving === 'org_profile' ? 'Saving…' : 'Save Profile'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Industry</Label>
                  <Input
                    value={kProfile.industry}
                    onChange={e => setKProfile(p => ({ ...p, industry: e.target.value }))}
                    placeholder="e.g. Professional Services"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Company Size</Label>
                  <Select
                    value={kProfile.company_size}
                    onValueChange={v => setKProfile(p => ({ ...p, company_size: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select size…" />
                    </SelectTrigger>
                    <SelectContent>
                      {COMPANY_SIZES.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>HQ Location</Label>
                  <Input
                    value={kProfile.hq_location}
                    onChange={e => setKProfile(p => ({ ...p, hq_location: e.target.value }))}
                    placeholder="e.g. New York, NY"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Current Plan / Deal Context</Label>
                <Textarea
                  value={kProfile.plan_details}
                  onChange={e => setKProfile(p => ({ ...p, plan_details: e.target.value }))}
                  placeholder="e.g. 30-seat pilot, monthly at $X. Decision point in Q2."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Key People */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-base">Key People</CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowPersonPicker(true)}>
                  <Plus className="h-3.5 w-3.5 mr-1" />Add Person
                </Button>
                <Button size="sm" onClick={savePeople} disabled={kSaving === 'key_person'}>
                  {kSaving === 'key_person' ? 'Saving…' : 'Save People'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {kPeople.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6 border border-dashed rounded-lg">
                  No key people added yet.{' '}
                  <button className="text-primary hover:underline" onClick={() => setShowPersonPicker(true)}>
                    Add the first one
                  </button>
                </p>
              ) : (
                kPeople.map((person) => (
                  <div key={person.localId} className="border rounded-xl p-4 space-y-3">
                    {/* Card header — profile badge or manual label */}
                    <div className="flex items-start justify-between gap-3">
                      {person.isFromProfile ? (
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">
                            {getInitials(person.name)}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{person.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{person.email}{person.department ? ` · ${person.department}` : ''}</p>
                          </div>
                          <Badge variant="outline" className="text-xs shrink-0 border-primary/30 text-primary">In Cosmo</Badge>
                        </div>
                      ) : (
                        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-1">
                          Not in Cosmo
                        </span>
                      )}
                      <Button
                        variant="ghost" size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => removePerson(person.localId)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>

                    {/* Name + Title — only editable for non-profile people */}
                    {!person.isFromProfile ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Full Name</Label>
                          <Input value={person.name} onChange={e => updatePerson(person.localId, 'name', e.target.value)} placeholder="e.g. Tom Ellis" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Title / Role</Label>
                          <Input value={person.title} onChange={e => updatePerson(person.localId, 'title', e.target.value)} placeholder="e.g. Head of Strategy" />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Title / Role</Label>
                        <Input value={person.title} onChange={e => updatePerson(person.localId, 'title', e.target.value)} placeholder="e.g. Head of Strategy" />
                      </div>
                    )}

                    {/* Enrichment selects */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Disposition</Label>
                        <Select value={person.disposition} onValueChange={v => updatePerson(person.localId, 'disposition', v)}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {DISPOSITIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Influence Level</Label>
                        <Select value={person.influence_level} onValueChange={v => updatePerson(person.localId, 'influence_level', v)}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {INFLUENCE_LEVELS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">AI Fluency</Label>
                        <Select value={person.ai_fluency_level} onValueChange={v => updatePerson(person.localId, 'ai_fluency_level', v)}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {AI_FLUENCY_LEVELS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs">Notes</Label>
                      <Textarea value={person.notes} onChange={e => updatePerson(person.localId, 'notes', e.target.value)} placeholder="Anything Cosmo should know about this person…" rows={2} />
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Goals */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-base">Goals</CardTitle>
              <Button size="sm" onClick={saveGoals} disabled={kSaving === 'goal'}>
                {kSaving === 'goal' ? 'Saving…' : 'Save Goals'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Primary 90-Day Goal</Label>
                <Textarea
                  value={kGoals.primary_goal}
                  onChange={e => setKGoals(g => ({ ...g, primary_goal: e.target.value }))}
                  placeholder="e.g. Get 80% of the team submitting at least 1 prompt per week"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Key Success Metric</Label>
                  <Input
                    value={kGoals.success_metric_90d}
                    onChange={e => setKGoals(g => ({ ...g, success_metric_90d: e.target.value }))}
                    placeholder="e.g. FSR above 4.0 for 10 users"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Target Deadline</Label>
                  <Input
                    value={kGoals.key_deadline}
                    onChange={e => setKGoals(g => ({ ...g, key_deadline: e.target.value }))}
                    placeholder="e.g. June 30, 2026"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Expansion Trigger</Label>
                <Input
                  value={kGoals.expansion_trigger}
                  onChange={e => setKGoals(g => ({ ...g, expansion_trigger: e.target.value }))}
                  placeholder="e.g. Expand from 30 to 100 seats when WAU > 25"
                />
              </div>
            </CardContent>
          </Card>

          {/* Organization Insights */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-base">Organization Insights</CardTitle>
              <Button size="sm" onClick={saveDynamics} disabled={kSaving === 'dynamic'}>
                {kSaving === 'dynamic' ? 'Saving…' : 'Save Insights'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-1.5">
                <Label>Notes</Label>
                <Textarea
                  value={kDynamics.notes}
                  onChange={e => setKDynamics(d => ({ ...d, notes: e.target.value }))}
                  placeholder="Politics, blockers, relationships between stakeholders, anything Cosmo should factor in…"
                  rows={4}
                />
              </div>

              {/* Documents */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>
                    Documents
                    <span className="text-muted-foreground text-xs font-normal ml-1.5">
                      PDF, Word, Excel · max {MAX_DOCS} files
                    </span>
                  </Label>
                  <span className="text-xs text-muted-foreground">{kDocs.length}/{MAX_DOCS}</span>
                </div>

                {/* File list */}
                {kDocs.length > 0 && (
                  <div className="space-y-1.5">
                    {kDocs.map(doc => (
                      <div
                        key={doc.localId}
                        className="flex items-center gap-3 px-3 py-2 border rounded-lg bg-muted/20"
                      >
                        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate font-medium">{doc.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatBytes(doc.size)}
                            {doc.file && (
                              <span className="ml-2 text-yellow-500 dark:text-yellow-400">
                                · pending upload
                              </span>
                            )}
                            {doc.url && !doc.file && (
                              <a
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-2 text-primary hover:underline"
                              >
                                View
                              </a>
                            )}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive shrink-0"
                          onClick={() => setKDocs(prev => prev.filter(d => d.localId !== doc.localId))}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add button */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_DOC_TYPES}
                  className="hidden"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (kDocs.length >= MAX_DOCS) {
                      toast.error(`Maximum ${MAX_DOCS} files allowed`);
                      return;
                    }
                    setKDocs(prev => [...prev, {
                      localId:  Math.random().toString(36).slice(2),
                      name:     f.name,
                      file:     f,
                      size:     f.size,
                      fileType: f.type,
                    }]);
                    // Reset so the same file can be re-added after removal
                    e.target.value = '';
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-dashed"
                  disabled={kDocs.length >= MAX_DOCS}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-3.5 w-3.5 mr-2" />
                  {kDocs.length === 0 ? 'Add a document' : '+ Add another document'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Users ── */}
        <TabsContent value="users" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Users</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Active</TableHead>
                    <TableHead>Submissions (30d)</TableHead>
                    <TableHead>Avg Score</TableHead>
                    <TableHead>FSR</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-muted-foreground">No users</TableCell>
                    </TableRow>
                  ) : (
                    users.map(user => (
                      <TableRow key={user.user_id}>
                        <TableCell className="font-medium">{user.full_name || '-'}</TableCell>
                        <TableCell className="text-muted-foreground">{user.email}</TableCell>
                        <TableCell><Badge variant="outline">{user.role}</Badge></TableCell>
                        <TableCell>{user.department}</TableCell>
                        <TableCell>
                          <Badge variant={user.is_active ? 'default' : 'secondary'}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {user.last_active
                            ? formatDistanceToNow(new Date(user.last_active), { addSuffix: true })
                            : 'Never'}
                        </TableCell>
                        <TableCell>{user.scored_30d}</TableCell>
                        <TableCell>{user.avg_score_30d || '-'}</TableCell>
                        <TableCell>{user.fsr || '-'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1 items-center">
                            <Button variant="ghost" size="sm" onClick={() => openEditUser(user)}>Edit</Button>
                            <UserActionsDropdown user={user} allUsers={users} onRefresh={fetchData} />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Settings ── */}
        <TabsContent value="settings" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Organization Settings</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Organization Name</Label>
                  <Input
                    value={editedName}
                    onChange={e => setEditedName(e.target.value)}
                    placeholder="Organization name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={editedStatus} onValueChange={setEditedStatus}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pilot">Pilot</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Internal Notes</Label>
                <Textarea
                  value={editedNotes}
                  onChange={e => setEditedNotes(e.target.value)}
                  placeholder="Notes about this client..."
                  rows={3}
                />
              </div>
              <Button onClick={saveOrgChanges} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Person Picker Dialog */}
      <Dialog open={showPersonPicker} onOpenChange={setShowPersonPicker}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Key Person</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Cosmo members */}
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {org?.org_name} Members
              </p>
              {availableUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">
                  {users.length === 0
                    ? 'No members signed up yet.'
                    : 'All members are already added as key people.'}
                </p>
              ) : (
                <div className="space-y-1.5">
                  {availableUsers.map(user => (
                    <button
                      key={user.user_id}
                      onClick={() => addPersonFromProfile(user)}
                      className="w-full text-left flex items-center gap-3 px-3 py-2.5 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">
                        {getInitials(user.full_name || user.email)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{user.full_name || user.email}</p>
                        <p className="text-xs text-muted-foreground">{user.role}{user.department ? ` · ${user.department}` : ''}</p>
                      </div>
                      <Badge variant="outline" className={`text-xs shrink-0 ${user.is_active ? 'border-green-300 text-green-700' : 'border-muted text-muted-foreground'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t pt-3">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => { setKPeople(prev => [...prev, newPerson()]); setShowPersonPicker(false); }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add someone not in Cosmo
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          {editingUser && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">{editingUser.email}</div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={editUserRole} onValueChange={setEditUserRole}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={editUserDept} onValueChange={setEditUserDept}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <Label>Workflows</Label>
                  <p className="text-xs text-muted-foreground">Allow access to the Workflow Builder</p>
                </div>
                <Switch checked={editUserWorkflows} onCheckedChange={setEditUserWorkflows} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
            <Button onClick={saveUserChanges}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
